// apps/provider/src/services/api/referral.js
// Referral source management — referring organizations and referring providers
//
// Xano tables: referring_organization, referring_provider

/**
 * Creates referral source methods (referring organizations + referring providers).
 * Mixed into ApiService via Object.assign().
 */
export function createReferralMethods() {
  return {

    // ==========================================
    // REFERRING ORGANIZATIONS
    // ==========================================

    /**
     * List referring organizations for the current organization.
     * Xano: GET /referring-organization
     * @param {Object} [filters]
     * @param {string} [filters.search_term] - Search by name
     * @param {string} [filters.status] - Filter by status (active/inactive)
     * @returns {Promise<import('@prism-ehr/shared').ReferringOrganizationResponse[]>}
     */
    async getReferringOrganizations(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.search_term) queryParams.append('search_term', filters.search_term);
      if (filters.status) queryParams.append('status', filters.status);
      const qs = queryParams.toString();
      return this.request(`/referring-organization${qs ? `?${qs}` : ''}`, { method: 'GET' });
    },

    /**
     * Get a single referring organization by ID.
     * Xano: GET /referring-organization/{id}
     * @param {number} id
     * @returns {Promise<import('@prism-ehr/shared').ReferringOrganizationResponse>}
     */
    async getReferringOrganization(id) {
      return this.request(`/referring-organization/${id}`, { method: 'GET' });
    },

    /**
     * Create a new referring organization.
     * Xano: POST /referring-organization
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').ReferringOrganizationResponse>}
     */
    async createReferringOrganization(data) {
      return this.request('/referring-organization', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Update a referring organization.
     * Xano: PATCH /referring-organization/{id}
     * @param {number} id
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').ReferringOrganizationResponse>}
     */
    async updateReferringOrganization(id, data) {
      return this.request(`/referring-organization/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Soft-delete a referring organization (sets status to inactive).
     * Xano: DELETE /referring-organization/{id}
     * @param {number} id
     * @returns {Promise<void>}
     */
    async deleteReferringOrganization(id) {
      return this.request(`/referring-organization/${id}`, { method: 'DELETE' });
    },

    // ==========================================
    // REFERRING PROVIDERS
    // ==========================================

    /**
     * List referring providers for the current organization.
     * Xano: GET /referring-provider
     * @param {Object} [filters]
     * @param {string} [filters.search_term] - Search by name or NPI
     * @param {string} [filters.status] - Filter by status
     * @param {number} [filters.referring_organization_id] - Filter by organization
     * @returns {Promise<import('@prism-ehr/shared').ReferringProviderResponse[]>}
     */
    async getReferringProviders(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.search_term) queryParams.append('search_term', filters.search_term);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.referring_organization_id) queryParams.append('referring_organization_id', filters.referring_organization_id);
      const qs = queryParams.toString();
      return this.request(`/referring-provider${qs ? `?${qs}` : ''}`, { method: 'GET' });
    },

    /**
     * Get a single referring provider by ID.
     * Xano: GET /referring-provider/{id}
     * @param {number} id
     * @returns {Promise<import('@prism-ehr/shared').ReferringProviderResponse>}
     */
    async getReferringProvider(id) {
      return this.request(`/referring-provider/${id}`, { method: 'GET' });
    },

    /**
     * Create a new referring provider.
     * Xano: POST /referring-provider
     * @param {Object} data - May include `pm_abbreviation` (Veradigm PM orgs only);
     *   creates a veradigm_pm_mapping (mapping_type "referring_provider") server-side.
     * @returns {Promise<import('@prism-ehr/shared').ReferringProviderResponse>}
     */
    async createReferringProvider(data) {
      return this.request('/referring-provider', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Update a referring provider.
     * Xano: PATCH /referring-provider/{id}
     * @param {number} id
     * @param {Object} data - `pm_abbreviation` (Veradigm PM orgs only) upserts the
     *   veradigm_pm_mapping; send "" to remove it, omit the key to leave it untouched.
     * @returns {Promise<import('@prism-ehr/shared').ReferringProviderResponse>}
     */
    async updateReferringProvider(id, data) {
      return this.request(`/referring-provider/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Soft-delete a referring provider (sets status to inactive).
     * Xano: DELETE /referring-provider/{id}
     * @param {number} id
     * @returns {Promise<void>}
     */
    async deleteReferringProvider(id) {
      return this.request(`/referring-provider/${id}`, { method: 'DELETE' });
    },

    // ==========================================
    // REFERRAL TRACKING (Phase 6)
    // ==========================================

    /**
     * List referrals for the current organization.
     * Xano: GET /referrals
     * @param {Object} [filters]
     * @param {string} [filters.status] - Filter by status
     * @param {string} [filters.discipline] - Filter by discipline (PT/OT/ST/multi)
     * @param {string} [filters.date_from] - Filter received_date >= date_from
     * @param {string} [filters.date_to] - Filter received_date <= date_to
     * @param {number} [filters.referring_provider_id] - Filter by referring provider
     * @param {number} [filters.referring_organization_id] - Filter by referring organization
     * @param {string} [filters.priority] - Filter by priority (routine/urgent)
     * @param {string} [filters.patient_id] - Filter by linked patient
     * @param {number} [filters.page] - Page number (default 1)
     * @param {number} [filters.limit] - Items per page (default 20)
     * @param {string} [filters.search_term] - Search patient name
     * @param {{sortBy: string, orderBy: string}} [filters.external] - Sort override,
     *   e.g. { sortBy: 'referral.received_date', orderBy: 'desc' }
     * @returns {Promise<import('@prism-ehr/shared').ReferralListResponse>}
     */
    async getReferrals(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.discipline) queryParams.append('discipline', filters.discipline);
      if (filters.date_from) queryParams.append('date_from', filters.date_from);
      if (filters.date_to) queryParams.append('date_to', filters.date_to);
      if (filters.referring_provider_id) queryParams.append('referring_provider_id', filters.referring_provider_id);
      if (filters.referring_organization_id) queryParams.append('referring_organization_id', filters.referring_organization_id);
      if (filters.priority) queryParams.append('priority', filters.priority);
      if (filters.patient_id) queryParams.append('patient_id', filters.patient_id);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.search_term) queryParams.append('search_term', filters.search_term);
      if (filters.external) queryParams.append('external', JSON.stringify(filters.external));
      const qs = queryParams.toString();
      return this.request(`/referrals${qs ? `?${qs}` : ''}`, { method: 'GET' });
    },

    /**
     * Get a single referral by ID with full joins.
     * Xano: GET /referrals/{referral_id}
     * @param {number} id
     * @returns {Promise<import('@prism-ehr/shared').ReferralResponse>}
     */
    async getReferral(id) {
      return this.request(`/referrals/${id}`, { method: 'GET' });
    },

    /**
     * Create a new referral.
     * Xano: POST /referrals
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').ReferralResponse>}
     */
    async createReferral(data) {
      return this.request('/referrals', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Update a referral.
     * Xano: PATCH /referrals/{referral_id}
     * @param {number} id
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').ReferralResponse>}
     */
    async updateReferral(id, data) {
      return this.request(`/referrals/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Soft-delete a referral (sets status to cancelled, stamps cancelled_at and the optional reason).
     * Xano: DELETE /referrals/{referral_id}
     * @param {number} id
     * @param {string} [reason] - Why the referral was cancelled
     * @returns {Promise<import('@prism-ehr/shared').ReferralResponse>}
     */
    async deleteReferral(id, reason) {
      const params = new URLSearchParams();
      if (reason) params.set('reason', reason);
      const qs = params.toString();
      return this.request(`/referrals/${id}${qs ? `?${qs}` : ''}`, { method: 'DELETE' });
    },

    /**
     * Convert a referral to a patient case (and optionally create patient + appointment).
     * Xano: POST /referrals-convert
     * @param {import('@prism-ehr/shared').ReferralConvertRequest} data
     * @returns {Promise<import('@prism-ehr/shared').ReferralResponse>}
     */
    async convertReferral(data) {
      return this.request('/referrals-convert', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Get referral summary counts for the current organization.
     * Xano: GET /referrals-summary
     * @returns {Promise<import('@prism-ehr/shared').ReferralSummary>}
     */
    async getReferralSummary() {
      return this.request('/referrals-summary', { method: 'GET' });
    },

    /**
     * List cases (with a projected patient summary) linked to one referral source.
     * Exactly one of referring_provider_id / referring_organization_id is required.
     * Xano: GET /referral-source/cases
     * @param {Object} filters
     * @param {number} [filters.referring_provider_id]
     * @param {number} [filters.referring_organization_id]
     * @param {'active'|'all'} [filters.status] - Default active; 'all' includes discharged
     * @param {number} [filters.page] - Page number (default 1)
     * @param {number} [filters.limit] - Items per page (default 50, max 100)
     * @returns {Promise<import('@prism-ehr/shared').ReferralSourceCaseListResponse>}
     */
    async getReferralSourceCases(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.referring_provider_id) queryParams.append('referring_provider_id', filters.referring_provider_id);
      if (filters.referring_organization_id) queryParams.append('referring_organization_id', filters.referring_organization_id);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      return this.request(`/referral-source/cases?${queryParams.toString()}`, { method: 'GET' });
    }
  };
}
